// UTM naming-convention ruleset — codifies the "Sked UTM Tracker — NAMING
// CONVENTIONS" spreadsheet (lowercase only, hyphens not spaces, no special
// chars, allowlists per parameter, banned-vague campaign values) as a typed,
// preset-driven engine.
//
// Pure: no IO, no React, no DOM. Same module runs server-side inside the
// Worker (`scoreRows` in score.ts) AND client-side inside the Builder for
// keystroke-time validation.
//
// Architecture:
//   - `Ruleset` is the typed data shape.
//   - `RULESETS` is a const map of seeded presets (`sked`/`generic`/`ecommerce`).
//   - `loadRuleset(presetId)` is the only entry point — keeps the seam open
//     for a future per-tenant `loadRuleset(tenant)` without changing call
//     sites.
//   - Evaluators (`evaluateGlobalFormat`, `evaluateVocabulary`,
//     `evaluateCampaignStructure`) are pure functions returning `RuleHit[]`.
//     Score.ts funnels each hit through its existing `addIssue` helper, so
//     the new categories integrate without restructuring the scorer.

// ---------------------------------------------------------------------------
// Types

export type FormatRuleId =
  | "lowercase"
  | "hyphens-not-spaces"
  | "no-underscores"
  | "no-special-chars";

export type RulesetCategory = "formatting" | "vocabulary" | "anti-pattern";

export type RuleSeverity = "severe" | "medium" | "mild";

export type UtmParam = "source" | "medium" | "campaign" | "content" | "term";

export interface Ruleset {
  /** Stable preset id — `sked` is default. */
  id: string;
  /** Bumped whenever the underlying convention spreadsheet changes. */
  version: string;
  /** Which global formatting rules apply to every UTM value. */
  formatRules: FormatRuleId[];
  /** Source allowlist + opt-in for the `partner-*` prefix shape. */
  source: { allowlist: string[]; partnerPrefix: boolean };
  /** Medium allowlist + values that are permitted but flagged as too generic. */
  medium: { allowlist: string[]; tooGeneric: string[] };
  /** Campaign banned-vague substrings + regex patterns. */
  campaign: { bannedSubstrings: string[]; bannedRegex: RegExp[] };
  /** Optional reference patterns surfaced as advice (not enforced). */
  content: { suggestedPatterns: string[] };
  term: { suggestedPatterns: string[] };
}

export interface RuleHit {
  category: RulesetCategory;
  /** Stable, scorer-friendly key (e.g. `uppercase-source`,
   *  `off-allowlist-medium-foo`). */
  key: string;
  severity: RuleSeverity;
  /** Short human-readable label for the issue list. */
  label: string;
  /** Why this rule fired on this value. */
  detail: string;
  /** Mechanical rewrite suggestion (e.g. `Google -> google`). Used by the AI
   *  prompt and the Builder's one-click "fix" affordance. */
  rewriteHint?: string;
}

// ---------------------------------------------------------------------------
// Format-rule predicates (mechanical, locale-agnostic)

const HAS_UPPERCASE = /[A-Z]/;
const HAS_SPACE = /\s/;
const HAS_UNDERSCORE = /_/;
// Disallow URL-unsafe + ambiguous characters per the convention spreadsheet.
// Permits letters / digits / `-` / `.` / `:` so values like `q1.2025` or
// `partner-geekout` pass. (`/` and `?` etc. are caught here too.)
const HAS_SPECIAL_CHARS = /[^a-zA-Z0-9\-.:]/;

// Mechanical canonicaliser used by the rewrite hint. Lowercases, replaces
// runs of whitespace/underscores with a single hyphen, strips disallowed
// characters, and collapses repeated hyphens. Pure — does not mutate.
export function canonicaliseValue(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9\-.:]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
}

// ---------------------------------------------------------------------------
// Sked preset — the convention spreadsheet, codified verbatim.

const SKED_SOURCE_ALLOWLIST = [
  "google",
  "facebook",
  "instagram",
  "linkedin",
  "tiktok",
  "youtube",
  "twitter-x",
  "bing",
  "email",
  "newsletter",
  "podcast",
  "sms",
  "direct",
  // Named partners called out in the spreadsheet.
  "partner-geekout",
  "partner-twism",
  "partner-girlpowermarketing",
];

const SKED_MEDIUM_ALLOWLIST = [
  // CSV-explicit medium vocabulary (broad marketing channels).
  "cpc",
  "cpm",
  "social",
  "email",
  "display",
  "video",
  "content",
  "referral",
  "affiliate",
  "podcast",
  "sms",
  "organic",
  "direct",
  // Sked-stricter tier — these are MORE specific than the CSV's `social`
  // and feed the leaderboard's per-channel grouping. Kept compliant so a
  // social-team-tagged URL with `organic-social` / `paid-social` doesn't
  // fire vocabulary:off-allowlist while still letting the CSV's broader
  // `social` value fall through to the too-generic warning.
  "organic-social",
  "paid-social",
  "influencer",
  "community",
];

// Substring-match (case-insensitive, post-canonicalise). The full agency-style
// list — Hugh's draft. Watch out: anything here flags any campaign/content/term
// containing the substring, so keep entries unambiguous.
const SKED_BANNED_SUBSTRINGS = [
  // CSV-explicit
  "campaign1",
  "test",
  "may-ads",
  // Numeric-suffixed test names — match `campaign1` through `campaign99` etc.
  // (covered also by the regex below; substring catches the literal too).
  // Generic placeholders / leftovers
  "test123",
  "qa",
  "staging",
  "stg",
  "dev",
  "temp",
  "tmp",
  "draft",
  "copy",
  "copy-of",
  "untitled",
  "tbd",
  "default",
  "placeholder",
  "foo",
  "bar",
  "foobar",
  "asdf",
  "lorem",
  "dummy",
  "sample",
  "example",
  // Month-ads pattern (vague — name the actual campaign instead).
  "january-ads",
  "february-ads",
  "march-ads",
  "april-ads",
  "june-ads",
  "july-ads",
  "august-ads",
  "september-ads",
  "october-ads",
  "november-ads",
  "december-ads",
  // Single / double / triple-letter dummies.
  "xx",
  "xxx",
];

const SKED_BANNED_REGEX = [
  /^test\d*$/i, // `test`, `test1`, `test42`
  /^campaign\d+$/i, // `campaign1` through `campaign99`
  /^\d{8}$/, // YYYYMMDD — unreadable
  /^banner\d{1,2}$/i, // `banner1`..`banner99`
  /^image\d{1,2}$/i, // `image1`..`image99`
  /^link\d{1,2}$/i, // `link1`..`link99`
  /^qa\d*$/i, // `qa`, `qa1`
  /^x{1,3}$/i, // `x`, `xx`, `xxx`
  /^ads?$/i, // `ad`, `ads`
];

const SKED_PRESET: Ruleset = {
  id: "sked",
  version: "csv-2026-04-27",
  formatRules: ["lowercase", "hyphens-not-spaces", "no-underscores", "no-special-chars"],
  source: { allowlist: SKED_SOURCE_ALLOWLIST, partnerPrefix: true },
  medium: {
    allowlist: SKED_MEDIUM_ALLOWLIST,
    // `social` is permitted by the spreadsheet but flagged as too generic;
    // the leaderboard groups by `organic-social` / `paid-social` so a more
    // specific value is more useful.
    tooGeneric: ["social"],
  },
  campaign: {
    bannedSubstrings: SKED_BANNED_SUBSTRINGS,
    bannedRegex: SKED_BANNED_REGEX,
  },
  content: {
    suggestedPatterns: [
      "{creative}-{variant}", // `headline-v1`
      "{format}-{differentiator}", // `carousel-blue`
      "cta-{label}", // `cta-free-trial`
      "{placement}-{type}", // `sidebar-banner`
    ],
  },
  term: {
    suggestedPatterns: [
      "{keyword}", // `social-media-scheduler`
      "{audience-label}", // `lookalike-30day-visitors`
      "{competitor}-alternative", // `hootsuite-alternative`
    ],
  },
};

// ---------------------------------------------------------------------------
// Generic preset — same global formatting, broader vocab, no banned-vague.

const GENERIC_SOURCE_EXTRAS = ["bing-ads", "reddit", "quora", "medium", "substack", "producthunt"];
const GENERIC_MEDIUM_EXTRAS = ["ppc", "paid", "retargeting"];

const GENERIC_PRESET: Ruleset = {
  id: "generic",
  version: "generic-2026-04-27",
  formatRules: ["lowercase", "hyphens-not-spaces", "no-underscores", "no-special-chars"],
  source: {
    allowlist: [...SKED_SOURCE_ALLOWLIST, ...GENERIC_SOURCE_EXTRAS],
    partnerPrefix: true,
  },
  medium: {
    allowlist: [...SKED_MEDIUM_ALLOWLIST, ...GENERIC_MEDIUM_EXTRAS],
    // Generic preset is intentionally less opinionated about `social`.
    tooGeneric: [],
  },
  campaign: { bannedSubstrings: [], bannedRegex: [] },
  content: { suggestedPatterns: SKED_PRESET.content.suggestedPatterns },
  term: { suggestedPatterns: SKED_PRESET.term.suggestedPatterns },
};

// ---------------------------------------------------------------------------
// Ecommerce preset — inherits sked + ecom-specific extensions.

const ECOM_MEDIUM_EXTRAS = ["affiliate", "comparison-shopping", "cashback"];
const ECOM_BANNED_EXTRAS = [
  "black-friday-sale", // missing year qualifier
  "sale", // single-word vague
  "promo",
  "discount",
];
const ECOM_PRESET: Ruleset = {
  id: "ecommerce",
  version: "ecom-2026-04-27",
  formatRules: SKED_PRESET.formatRules,
  source: { ...SKED_PRESET.source },
  medium: {
    allowlist: [...new Set([...SKED_MEDIUM_ALLOWLIST, ...ECOM_MEDIUM_EXTRAS])],
    tooGeneric: SKED_PRESET.medium.tooGeneric,
  },
  campaign: {
    bannedSubstrings: [...SKED_BANNED_SUBSTRINGS, ...ECOM_BANNED_EXTRAS],
    bannedRegex: SKED_BANNED_REGEX,
  },
  content: {
    suggestedPatterns: [
      ...SKED_PRESET.content.suggestedPatterns,
      "sku-{sku}-{variant}", // `sku-1234-red`
    ],
  },
  term: { suggestedPatterns: SKED_PRESET.term.suggestedPatterns },
};

// ---------------------------------------------------------------------------
// Public surface

export const RULESETS: Record<string, Ruleset> = {
  sked: SKED_PRESET,
  generic: GENERIC_PRESET,
  ecommerce: ECOM_PRESET,
};

export const DEFAULT_RULESET_ID = "sked";

/** Resolve a `Ruleset` by preset id. Unknown ids fall back to the default
 *  preset (`sked`) — never throws so call sites can pass user-controlled
 *  strings safely. The seam exists for a future per-tenant override. */
export function loadRuleset(presetId: string = DEFAULT_RULESET_ID): Ruleset {
  return RULESETS[presetId] ?? RULESETS[DEFAULT_RULESET_ID];
}

// ---------------------------------------------------------------------------
// Evaluators

/** Evaluate global formatting rules (lowercase / hyphens / no-special-chars)
 *  for a single UTM value on a given parameter. Returns one `RuleHit` per
 *  triggered rule. Empty / undefined values produce no hits — required-param
 *  enforcement lives in the scorer, not here. */
export function evaluateGlobalFormat(
  value: string | undefined | null,
  param: UtmParam,
  ruleset: Ruleset,
): RuleHit[] {
  if (!value) return [];
  const hits: RuleHit[] = [];

  if (ruleset.formatRules.includes("lowercase") && HAS_UPPERCASE.test(value)) {
    hits.push({
      category: "formatting",
      key: `uppercase-${param}`,
      severity: "mild",
      label: `Uppercase in utm_${param}`,
      detail: `'${value}' contains uppercase letters; the convention requires lowercase only`,
      rewriteHint: `${value} -> ${canonicaliseValue(value)}`,
    });
  }

  if (ruleset.formatRules.includes("hyphens-not-spaces") && HAS_SPACE.test(value)) {
    hits.push({
      category: "formatting",
      key: `has-spaces-${param}`,
      severity: "medium",
      label: `Spaces in utm_${param}`,
      detail: `'${value}' contains whitespace; use hyphens to separate words`,
      rewriteHint: `${value} -> ${canonicaliseValue(value)}`,
    });
  }

  if (ruleset.formatRules.includes("no-underscores") && HAS_UNDERSCORE.test(value)) {
    hits.push({
      category: "formatting",
      key: `has-underscore-${param}`,
      severity: "mild",
      label: `Underscore in utm_${param}`,
      detail: `'${value}' contains an underscore; use hyphens instead`,
      rewriteHint: `${value} -> ${canonicaliseValue(value)}`,
    });
  }

  if (ruleset.formatRules.includes("no-special-chars") && HAS_SPECIAL_CHARS.test(value)) {
    hits.push({
      category: "formatting",
      key: `has-special-chars-${param}`,
      severity: "medium",
      label: `Special characters in utm_${param}`,
      detail: `'${value}' contains URL-unsafe or ambiguous characters; use letters/digits/hyphens`,
      rewriteHint: `${value} -> ${canonicaliseValue(value)}`,
    });
  }

  return hits;
}

/** Evaluate the controlled vocabulary for `source` / `medium`. Off-allowlist
 *  values fire `vocabulary:off-allowlist-*`; the medium-only `tooGeneric`
 *  list fires the milder `vocabulary:too-generic-*`. */
export function evaluateVocabulary(
  value: string | undefined | null,
  param: "source" | "medium",
  ruleset: Ruleset,
): RuleHit[] {
  if (!value) return [];
  // Compare in lowercase so a casing violation also producing a vocabulary
  // hit doesn't double-fire when the lowercased value IS in the allowlist.
  const lower = value.toLowerCase();
  const hits: RuleHit[] = [];

  if (param === "source") {
    const onAllowlist = ruleset.source.allowlist.includes(lower);
    const allowedPartner = ruleset.source.partnerPrefix && lower.startsWith("partner-");
    if (!onAllowlist && !allowedPartner) {
      hits.push({
        category: "vocabulary",
        key: `off-allowlist-source-${lower}`,
        severity: "medium",
        label: `'${value}' is not in the source vocabulary`,
        detail: `utm_source='${value}' is not on the ${ruleset.id} allowlist`,
      });
    }
    return hits;
  }

  // medium
  const onAllowlist = ruleset.medium.allowlist.includes(lower);
  const tooGeneric = ruleset.medium.tooGeneric.includes(lower);
  if (!onAllowlist) {
    hits.push({
      category: "vocabulary",
      key: `off-allowlist-medium-${lower}`,
      severity: "medium",
      label: `'${value}' is not in the medium vocabulary`,
      detail: `utm_medium='${value}' is not on the ${ruleset.id} allowlist`,
    });
  } else if (tooGeneric) {
    hits.push({
      category: "vocabulary",
      key: `too-generic-${lower}`,
      severity: "mild",
      label: `'${value}' is permitted but generic`,
      detail: `utm_medium='${value}' is allowed but '${value === "social" ? "organic-social / paid-social" : "a more specific value"}' is more useful`,
    });
  }
  return hits;
}

/** Evaluate campaign-structure anti-patterns: banned-vague substrings and
 *  banned regex (e.g. YYYYMMDD dates, `campaign\d+`). */
export function evaluateCampaignStructure(
  value: string | undefined | null,
  ruleset: Ruleset,
): RuleHit[] {
  if (!value) return [];
  const lower = value.toLowerCase();
  const hits: RuleHit[] = [];

  for (const banned of ruleset.campaign.bannedSubstrings) {
    if (lower.includes(banned)) {
      hits.push({
        category: "anti-pattern",
        key: `generic-name-${banned}`,
        severity: "medium",
        label: `Generic / placeholder campaign name`,
        detail: `'${value}' contains the banned token '${banned}' — pick a specific name`,
      });
      // Stop after the first banned-substring hit per row to avoid pile-on.
      break;
    }
  }

  for (const re of ruleset.campaign.bannedRegex) {
    if (re.test(lower)) {
      // Distinguish the YYYYMMDD case so the AI prompt can give a targeted
      // rewrite suggestion ("use apr-2025 instead of 20250401").
      const isYyyymmdd = /^\d{8}$/.test(lower);
      hits.push({
        category: "anti-pattern",
        key: isYyyymmdd ? "yyyymmdd-date" : `regex-${re.source}`,
        severity: isYyyymmdd ? "mild" : "medium",
        label: isYyyymmdd ? "YYYYMMDD date in campaign" : "Generic / placeholder campaign name",
        detail: isYyyymmdd
          ? `'${value}' is hard to read — prefer 'apr-2025' or 'q2-2025'`
          : `'${value}' matches a banned campaign-name pattern`,
      });
      break;
    }
  }

  return hits;
}
